import { APP_LOCAL_KEYS, APP_NAME } from "@/app/constants/constants";
import { WareHouseConstants } from "@/app/constants/ware-housing.constants";
import type { CommandPaletteRecentStored } from "@/components/commandPalette/commandPalette.types";
import { MESSAGE_COUNT } from "@/features/messages/constants/messages.constants";
import { ProjectRegisterKeys } from "@/features/projectRegister/projectRegister.constants";
import { ProjectRegisterLocalStorageKeys } from "@/features/projectRegister/projectRegister.enums";
import { TaskAndClaimsConstantsLocalKeys } from "@/features/taskAndClaims/taskAndClaims.types";
import { ToolsKeys } from "@/features/tools/constants/tools.constants";

type JsonLike = unknown;

type DataTableColumnOrderStorageKey =
  `${typeof APP_NAME}_table_column_order_${string}`;

type BaseLocalStorageState = {
  [APP_LOCAL_KEYS.SUB_DOMAIN]: string;
  [APP_LOCAL_KEYS.USER_DATA]: JsonLike;
  [APP_LOCAL_KEYS.USER_PERMISSIONS]: JsonLike;
  [APP_LOCAL_KEYS.TOKEN]: string;
  [APP_LOCAL_KEYS.CUSTOMER_ID]: string;
  [APP_LOCAL_KEYS.CUSTOMER_USER_ID]: string;
  [APP_LOCAL_KEYS.CUSTOMER_DATA]: JsonLike;
  [APP_LOCAL_KEYS.CUSTOMER_AUTH_USER]: JsonLike;
  [APP_LOCAL_KEYS.ADMIN_AS_CUSTOMER]: JsonLike;
  [APP_LOCAL_KEYS.IS_ADMIN_LOGIN_AS_CUSTOMER]: boolean;
  [APP_LOCAL_KEYS.WORKSPACE_DATA]: JsonLike;
  [APP_LOCAL_KEYS.WORKSPACE_ID]: string;
  [APP_LOCAL_KEYS.CUSTOMER_PORTAL_TOKEN]: string;
  [APP_LOCAL_KEYS.REDIRECT_PATH]: string;
  [APP_LOCAL_KEYS.COOKIE_SESSION]: boolean;
  [APP_LOCAL_KEYS.COMMAND_PALETTE_RECENT]: CommandPaletteRecentStored[];
  [MESSAGE_COUNT]: number;
  [ToolsKeys.SUB_PRODUCT_NAME]: string;
  [TaskAndClaimsConstantsLocalKeys.SHEET_DATA]: JsonLike;
  [ProjectRegisterLocalStorageKeys.SHEET_DATA]: JsonLike;
  [ProjectRegisterKeys.lastReferenceNumber]: string | number;
  [WareHouseConstants.KEYS.WAREHOUSE_DETAILS]: JsonLike;
  employeeSheetName: string;
  terminatingUserDetails: JsonLike;
  tickets: unknown[];
  latestReferenceNo: string;
  isFullScreen: "true" | "false";
  leadsId: string;
  "vite-ui-theme": "dark" | "light" | "system";
};

type LocalStorageState = BaseLocalStorageState &
  Record<DataTableColumnOrderStorageKey, string[]>;

export type LocalStorageKey = Extract<keyof LocalStorageState, string>;

type LocalStorageValue<TKey extends LocalStorageKey> = LocalStorageState[TKey];

const jsonStorageKeys = new Set<LocalStorageKey>([
  APP_LOCAL_KEYS.USER_DATA,
  APP_LOCAL_KEYS.USER_PERMISSIONS,
  APP_LOCAL_KEYS.CUSTOMER_DATA,
  APP_LOCAL_KEYS.CUSTOMER_AUTH_USER,
  APP_LOCAL_KEYS.ADMIN_AS_CUSTOMER,
  APP_LOCAL_KEYS.WORKSPACE_DATA,
  APP_LOCAL_KEYS.COMMAND_PALETTE_RECENT,
  TaskAndClaimsConstantsLocalKeys.SHEET_DATA,
  ProjectRegisterLocalStorageKeys.SHEET_DATA,
  WareHouseConstants.KEYS.WAREHOUSE_DETAILS,
  "terminatingUserDetails",
  "tickets",
]);

const isDataTableColumnOrderStorageKey = (
  key: LocalStorageKey,
): key is DataTableColumnOrderStorageKey => {
  return key.startsWith(`${APP_NAME}_table_column_order_`);
};

const browserLocalStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage;
};

const readRaw = (key: LocalStorageKey): string | null => {
  try {
    return browserLocalStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeJsonParse = <T>(rawValue: string | null): T | null => {
  if (rawValue == null || rawValue === "") {
    return null;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return null;
  }
};

const parseStoredValue = <TKey extends LocalStorageKey>(
  key: TKey,
  rawValue: string | null,
): LocalStorageValue<TKey> | null => {
  if (rawValue == null) {
    return null;
  }

  if (jsonStorageKeys.has(key) || isDataTableColumnOrderStorageKey(key)) {
    return safeJsonParse<LocalStorageValue<TKey>>(rawValue);
  }

  if (
    key === APP_LOCAL_KEYS.IS_ADMIN_LOGIN_AS_CUSTOMER ||
    key === APP_LOCAL_KEYS.COOKIE_SESSION
  ) {
    if (rawValue === "true") {
      return true as LocalStorageValue<TKey>;
    }

    if (rawValue === "false") {
      return false as LocalStorageValue<TKey>;
    }

    const parsed = safeJsonParse<boolean>(rawValue);

    return (parsed ?? false) as LocalStorageValue<TKey>;
  }

  if (key === MESSAGE_COUNT) {
    const parsed = safeJsonParse<number>(rawValue);
    if (typeof parsed === "number" && Number.isFinite(parsed)) {
      return parsed as LocalStorageValue<TKey>;
    }

    const numeric = Number(rawValue);

    return (Number.isFinite(numeric) ? numeric : 0) as LocalStorageValue<TKey>;
  }

  return rawValue as LocalStorageValue<TKey>;
};

const serializeStoredValue = <TKey extends LocalStorageKey>(
  key: TKey,
  storageValue: LocalStorageValue<TKey>,
): string => {
  if (jsonStorageKeys.has(key) || isDataTableColumnOrderStorageKey(key)) {
    return JSON.stringify(storageValue);
  }

  if (
    typeof storageValue === "string" ||
    typeof storageValue === "number" ||
    typeof storageValue === "boolean"
  ) {
    return String(storageValue);
  }

  return JSON.stringify(storageValue);
};

export interface TypedLocalStorage {
  getItem: <TKey extends LocalStorageKey>(
    key: TKey,
  ) => LocalStorageValue<TKey> | null;
  getItemOrDefault: <TKey extends LocalStorageKey>(
    key: TKey,
    defaultValue: LocalStorageValue<TKey>,
  ) => LocalStorageValue<TKey>;
  getJsonOrDefault: <TValue extends JsonLike>(
    key: LocalStorageKey,
    defaultValue: TValue,
  ) => TValue;
  setItem: <TKey extends LocalStorageKey>(
    key: TKey,
    storageValue: LocalStorageValue<TKey>,
  ) => void;
  removeItem: <TKey extends LocalStorageKey>(key: TKey) => void;
  clearAll: () => void;
}

/** Safe localStorage adapter that normalizes typed application values. */
export const typedLocalStorage: TypedLocalStorage = {
  getItem<TKey extends LocalStorageKey>(
    key: TKey,
  ): LocalStorageValue<TKey> | null {
    return parseStoredValue(key, readRaw(key));
  },

  getItemOrDefault<TKey extends LocalStorageKey>(
    key: TKey,
    defaultValue: LocalStorageValue<TKey>,
  ): LocalStorageValue<TKey> {
    const storedValue = this.getItem(key);

    if (storedValue == null || storedValue === "") {
      return defaultValue;
    }

    return storedValue;
  },

  getJsonOrDefault<TValue extends JsonLike>(
    key: LocalStorageKey,
    defaultValue: TValue,
  ): TValue {
    const storedValue = this.getItem(key);

    if (storedValue == null || storedValue === "") {
      return defaultValue;
    }

    return storedValue as TValue;
  },

  setItem<TKey extends LocalStorageKey>(
    key: TKey,
    storageValue: LocalStorageValue<TKey>,
  ): void {
    try {
      browserLocalStorage()?.setItem(
        key,
        serializeStoredValue(key, storageValue),
      );
    } catch {
      // best-effort persistence
    }
  },

  removeItem<TKey extends LocalStorageKey>(key: TKey): void {
    try {
      browserLocalStorage()?.removeItem(key);
    } catch {
      // best-effort cleanup
    }
  },

  clearAll(): void {
    try {
      browserLocalStorage()?.clear();
    } catch {
      // best-effort cleanup
    }
  },
};
